import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import type { RouteRecord } from 'vite-react-ssg'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { captureEvent, capturePageview } from './lib/analytics'
import ContactPage from './pages/ContactPage'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import NotFoundPage from './pages/NotFoundPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import MissionPage from './pages/MissionPage'
import SecurityPage from './pages/SecurityPage'
import EndometriosisPage from './pages/learn/EndometriosisPage'
import AdenomyosisPage from './pages/learn/AdenomyosisPage'
import ExcisionSurgeryPage from './pages/learn/ExcisionSurgeryPage'
import TreatmentPage from './pages/learn/TreatmentPage'
import PelviaCarePage from './pages/PelviaCarePage'
import PatientLayout from './pages/patient/PatientLayout'
import BriefingPage from './pages/patient/BriefingPage'
import CoachPage from './pages/patient/CoachPage'
import CheckinPage from './pages/patient/CheckinPage'
import IntakePage from './pages/patient/IntakePage'
import ProfilePage from './pages/patient/ProfilePage'
import SettingsPage from './pages/patient/SettingsPage'
import IntegrationsPage from './pages/patient/IntegrationsPage'

// One client for the whole app. Lives at module scope so it survives re-renders
// and is shared across every route. (Previously in main.tsx, moved here so the
// provider can sit inside the SSG route tree's root layout.)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
})

// Map a /patient route to the old SPA tab name, so the `tab_switched` event
// keeps the same labels it had before the React migration.
function tabForPath(pathname: string): string | null {
  if (pathname === '/patient' || pathname === '/patient/') return 'briefing'
  const m = pathname.match(/^\/patient\/([^/]+)/)
  return m ? m[1] : null
}

// Drives analytics off the router: a SPA pageview on every location change,
// plus the legacy `tab_switched` event when moving between patient sections.
function RouteAnalytics() {
  const { pathname } = useLocation()
  const prevTab = useRef<string | null>(null)

  useEffect(() => {
    capturePageview()

    const tab = tabForPath(pathname)
    if (tab && tab !== prevTab.current) {
      captureEvent('tab_switched', { tab })
    }
    prevTab.current = tab
  }, [pathname])

  return null
}

// React Router preserves scroll position across client-side navigations, so moving
// between pages (e.g. clicking "PelviaCare+" in the header) leaves you wherever you
// were scrolled on the previous page. Reset to the top on each pathname change.
//
// Hash links (e.g. /patient/profile#medications) need extra work: client-side
// navigation doesn't fire the browser's native scroll-to-fragment, so we scroll to
// the anchor ourselves. The target section may still be mounting (pages like
// ProfilePage gate on a data fetch), so we poll a few frames for it, and offset by
// the fixed 56px topbar so the section heading isn't hidden underneath it.
const HASH_SCROLL_OFFSET = 72

function ScrollToTop() {
  const { pathname, hash } = useLocation()

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1))
      let frame = 0
      let attempts = 0
      const tryScroll = () => {
        const el = document.getElementById(id)
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - HASH_SCROLL_OFFSET
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
          return
        }
        // ~2s worth of frames — enough for a cold page/data load to mount the anchor.
        if (attempts++ < 120) frame = requestAnimationFrame(tryScroll)
      }
      frame = requestAnimationFrame(tryScroll)
      return () => cancelAnimationFrame(frame)
    }
    window.scrollTo(0, 0)
  }, [pathname, hash])

  return null
}

// Root layout for the data router: provides the shared TanStack Query client and
// router-driven analytics, then renders the matched route via <Outlet/>. Browser-
// only side effects stay in child components/effects so this renders safely during
// static site generation (SSG).
function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouteAnalytics />
      <ScrollToTop />
      <Outlet />
    </QueryClientProvider>
  )
}

// Route table as data-router records (consumed by vite-react-ssg). Which of these
// get pre-rendered to static HTML is controlled by `ssgOptions.includedRoutes` in
// vite.config.ts — the public marketing routes are rendered; /patient/* and the
// auth routes stay client-only.
export const routes: RouteRecord[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: 'login', element: <LoginPage /> },
      { path: 'contact', element: <ContactPage /> },
      { path: 'privacy', element: <PrivacyPage /> },
      { path: 'terms', element: <TermsPage /> },
      { path: 'mission', element: <MissionPage /> },
      // Renamed from /team — keep the old path working for existing links & SEO.
      { path: 'team', element: <Navigate to="/mission" replace /> },
      { path: 'security', element: <SecurityPage /> },
      { path: 'endometriosis', element: <EndometriosisPage /> },
      { path: 'adenomyosis', element: <AdenomyosisPage /> },
      { path: 'excision-surgery', element: <ExcisionSurgeryPage /> },
      { path: 'treatment', element: <TreatmentPage /> },
      { path: 'pelviacare', element: <PelviaCarePage /> },
      // Renamed from /tracking (the old app page) — keep the path for old links & SEO.
      { path: 'tracking', element: <Navigate to="/pelviacare" replace /> },
      { path: 'patient/intake', element: <IntakePage /> },
      {
        path: 'patient',
        element: <PatientLayout />,
        children: [
          { index: true, element: <BriefingPage /> },
          // Calendar was merged into the dashboard; old links 301 to /patient.
          { path: 'calendar', element: <Navigate to="/patient" replace /> },
          // Pattern analytics were merged into the dashboard; old links 301 to /patient.
          { path: 'pattern', element: <Navigate to="/patient" replace /> },
          { path: 'coach', element: <CoachPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'checkin', element: <CheckinPage /> },
          { path: 'integrations', element: <IntegrationsPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]
